import { Container, Row, Card, CardBody, Col, Form, FormGroup, Label, Input, Button, Modal, ModalHeader, ModalBody } from "reactstrap";
import CommonCardHeader from "../../../CommonElements/CommonCardHeader/CommonCardHeader";
import { Fragment, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Btn, H3, H5, P } from "../../../AbstractElements";
import { toast } from "react-toastify";
import { downloadPatientDocument, getSinglePatientAction } from "../../../Api/patient";
import GeneratingLoader from "../../../CommonElements/GeneratingLoader/GeneratingLoader";
import PatientDocuments from "./PatientDocuments";
import { generateReportAPI } from "../../../Api/report";

const PatientDetailsContainer = () => {
  const [searchParams] = useSearchParams();
  const patient_id = searchParams.get("patient_id") || "";
  const [patient, setPatient] = useState<any>(null);
  const [loader, setLoader] = useState<boolean>(false);
  const [note, setNote] = useState("");
  const [template, setTemplate] = useState("");
  const [modal, setModal] = useState(false);  // Modal visibility state
  const [reportContent, setReportContent] = useState("");  // State to store report content

  const downloadDocument = async (patientId: any) => {
    try {
      const response = await downloadPatientDocument(patientId);
      if (response.status === "success") {
        window.open(response.payload?.fileUrl);
      } else {
        toast.error("This document is not available!");
      }
    } catch (error) {
      console.error("Error fetching case details:", error);
    }
  };

  const generateReport = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoader(true);

    try {
      const response = await generateReportAPI({ template, note, patient_id: patient_id });
      if (response?.data?.status === "success") {
        toast.success("Report generated successfully!");
        setReportContent(response?.data?.payload?.content);  // Set the report content in state
        setModal(true);  // Show the modal after successful report generation
      } else {
        toast.error("Report generation failed!");
      }

    } catch (error) {
      toast.error("Error!");
    }

    setLoader(false);
  };

  const fetchPatientDetails = async () => {
    setLoader(true);

    try {
      const response = await getSinglePatientAction(patient_id);
      if (response.status === "success") {
        setPatient(response.payload.payload);
      } else {
        console.error("Failed to fetch patient details:", response.message);
      }
    } catch (error) {
      console.error("Error fetching patient details:", error);
    }

    setLoader(false);
  };

  useEffect(() => {
    if (patient_id) {
      fetchPatientDetails();
    }
  }, [patient_id]);

  // Function to handle closing the modal
  const toggleModal = () => setModal(!modal);

  return (
    <Container fluid className="default-dashboard dashboard-2">
      <Row className="widget-grid align-items-stretch">
        <Col xl="5" className="mb-4">
          <Card className="py-3">
            {loader && <GeneratingLoader label="Loading..." />}
            <CommonCardHeader headClass="card-no-border" title={`Patient Information`} />
            <div className="m-l-25">
              <CardBody className="projects px-0 pt-2">
                <H5 className="mb-3">
                  <b>Name:</b> {patient?.firstname + " " + patient?.lastname}
                </H5>
                <H5 className="mb-3">
                  <b>{patient?.type === 'child' && 'Parent '}Email:</b> {patient?.email}
                </H5>
                <H5 className="mb-3">
                  <b>{patient?.type === 'child' && 'Parent '}Phone Number:</b> {patient?.phonenumber}
                </H5>
                <H5 className="mb-3">
                  <b>Birthday:</b>{" "}
                  {new Date(patient?.birthday).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long", // Full month name (e.g., "January")
                    day: "numeric", // Day of the month
                  })}{" "}
                </H5>
                <H5 className="mb-3">
                  <b>Address:</b> {patient?.address}
                </H5>
                {patient?.type === "child" && (
                  <Fragment>
                    <H5 className="mb-3">
                      <b>School:</b> {patient?.school}
                    </H5>
                    <H5 className="mb-3">
                      <b>Teacher Email:</b> {patient?.teacher_email}
                    </H5>
                    <H5 className="mb-3">
                      <b>GP Email:</b> {patient?.gp_email}
                    </H5>
                  </Fragment>
                )}
              </CardBody>
            </div>

          </Card>
          <Card className="py-3 mb-0">
            <CardBody className="projects pt-2">
              <PatientDocuments
                patientId={patient_id}
                downloadDocument={(documentId) =>
                  downloadDocument(documentId)
                }
              />
            </CardBody>
          </Card>
        </Col>

        {/* Report Generation Form */}
        <Col xl="7" className="mb-4">
          <Card className="py-3 h-100">
            <CommonCardHeader headClass="card-no-border" title={`Generate Report`} />
            <div className="px-4 h-100 d-flex flex-column">
              <CardBody className="projects px-0 pt-2 flex-grow-1 d-flex flex-column w-100" style={{ alignItems: "normal" }}>
                <Form className="theme-form d-flex flex-column flex-grow-1" onSubmit={generateReport}>
                  <FormGroup className="w-100">
                    <Label className="col-form-label">{"Select a template"}</Label>
                    <Input
                      type="select"
                      className="w-100"
                      required
                      name="email"
                      onChange={(event) => setTemplate(event.target.value)}
                    >
                      <option value="" disabled selected>
                        ---Please choose a template---
                      </option>
                      <option value="adhd">ADHD</option>
                      <option value="asd">ASD Template</option>
                    </Input>
                  </FormGroup>

                  <FormGroup className="flex-grow-1 d-flex flex-column w-100">
                    <Label className="col-form-label">{"Note"}</Label>
                    <div className="form-input position-relative flex-grow-1 d-flex flex-column w-100">
                      <Input
                        type="textarea"
                        name="note"
                        required
                        value={note}
                        onChange={(event) => setNote(event.target.value)}
                        className="flex-grow-1 w-100"
                        style={{ minHeight: "280px" }}
                        placeholder="Enter notes while you are in consultation"
                      />
                    </div>
                  </FormGroup>

                  <FormGroup className="mb-0 form-sub-title d-flex justify-content-end w-100">
                    <div className="text-end mt-3" style={{ width: "120px" }}>
                      <Button color="primary" block type="submit">
                        {"Generate"}
                      </Button>
                    </div>
                  </FormGroup>
                </Form>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Modal to Display Generated Report */}
      <Modal isOpen={modal} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>Generated Report</ModalHeader>
        <ModalBody>
          <div style={{ whiteSpace: "pre-wrap" }}>
            {/* Render the report content with preserved formatting */}
            {reportContent}
          </div>
        </ModalBody>
      </Modal>
    </Container>
  );
};

export default PatientDetailsContainer;
